import React from "react";
import { toast } from "sonner";

import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { confirmDangerCls, confirmDialogCls } from "@/shared/components/settings-atoms";
import { useDeleteInterface } from "@/shared/api/interfaces";
import { catalogFor } from "@/shared/lib/interface-catalog";

import type { Interface } from "@/shared/types";

/* Disconnecting logs the bot out and unlinks the number at the provider, so
   there is no undo to offer — it asks first, and says so. */
export function DisconnectDialog({ iface, agentName, onClose }: {
  iface: Interface;
  agentName: string;
  onClose: () => void;
}) {
  const remove = useDeleteInterface();
  const name = iface.label ?? catalogFor(iface.type)?.name ?? "this interface";

  return (
    <AlertDialog open onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent className={`${confirmDialogCls} max-w-[420px]`}>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[15px] tracking-[-0.01em]">Disconnect {name}?</AlertDialogTitle>
          <AlertDialogDescription className="text-[13px] leading-[1.6]">
            {agentName} stops answering there, and it is logged out of Definable. There is no undo — connecting it
            again means setting it up from the start.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full text-[13px]">Cancel</AlertDialogCancel>
          <button
            className={confirmDangerCls}
            disabled={remove.isPending}
            onClick={() =>
              remove.mutate(iface.id, {
                onSuccess: () => {
                  toast.success(`${name} disconnected`);
                  onClose();
                },
              })
            }
          >
            {remove.isPending ? "Disconnecting…" : "Disconnect"}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
